import { buildClassName } from '@bluebeam/build-class-name';
import { useTranslator } from '@bluebeam/react-i18n';
import { useAuth } from 'auth';
import { FeatureFlag } from 'featureFlags';
import LanguageIcon from 'components/Icons/LanguageIcon';
import NewWindow from 'components/Icons/NewWindow';
import Profile from 'components/Icons/Profile';
import { SelectOption } from 'components/inputs/BaseSelect';
import EditUserProfileInfoModal from 'components/modals/EditUserProfileInfoModal';
import LanguageSettingsModal from 'components/modals/LanguageSettingsModal';
import PrivacySettingsModal from 'components/modals/PrivacySettingsModal';
import OutsideClick from 'components/OutsideClick';
import { environmentVars } from 'constants/services';
import AppStoreContext from 'contexts/AppStoreContext';
import { useDIContainer } from 'hooks/useDIContainer';
import {
  withProjectManager,
  WithProjectManagerProps,
} from 'providers/ProjectManagerProvider';
import React, { useContext, useEffect, useState } from 'react';
import { Manager, Popper, Reference } from 'react-popper';
import {
  RouteComponentProps,
  withRouter,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { ModalState } from 'records/ModalState';
import { getAppServices } from 'providers/app-services';
import { useUserInfo } from 'providers/UserInfoProvider';
import { LicensePackageOptions } from 'records/License';
import { useLanguageOptions } from 'hooks/useLanguageOptions';

type Props = RouteComponentProps & WithProjectManagerProps;

const accountLink = environmentVars.BB_OPENID_CREATE_USER_ENDPOINT.substr(
  0,
  environmentVars.BB_OPENID_CREATE_USER_ENDPOINT.length -
    '/createaccount'.length
);

const UserCircle = (props: Props) => {
  const { translate } = useTranslator(['userDropdown']);
  const [open, setOpen] = useState(false);
  const [languageSettingsOpen, setLanguageSettingsOpen] =
    useState<boolean>(false);
  const { id: selectedProject } = props.projectManager.getSelectedProject();
  const context = useContext(AppStoreContext);
  const [selectedLanguage, setSelectedLanguage] =
    useState<SelectOption<string>>() || '';
  const localStorageLangName = 'BB_LANGUAGE_CODE';
  const languageCode = localStorage.getItem(localStorageLangName);
  const { authService } = useDIContainer();
  const { userId } = useAuth();
  const { languageOptions } = useLanguageOptions();
  const location = useLocation();
  const history = useHistory();

  const { featureFlagService } = getAppServices();
  const isManageAccountIntegrationsEnabled = featureFlagService.hasFeature(
    FeatureFlag.ManageAccountIntegrations
  );
  const canManageConnectedAccounts =
    useUserInfo().getLicense().package !== LicensePackageOptions.Unpaid;

  useEffect(() => {
    setOpen(false);
    setLanguageSettingsOpen(false);
  }, [location]);

  useEffect(() => {
    const currentLanguage = languageOptions.find(
      (language) => language.value === languageCode
    ) || { label: '', description: '', value: '' };

    setSelectedLanguage(currentLanguage);
  }, [languageCode]);

  const handleUserCircleClick = (): void => {
    setOpen(!open);
  };

  const handleOutsideClick = (): void => {
    setOpen(false);
  };

  const handleLogoutClick = (): void => {
    authService.logout();
  };

  const handleEditPrivacySettingsClick = (): void => {
    setOpen(false);

    const modalState = new ModalState({
      id: 'privacySettingsModal',
      portal: 'main',
      open: true,
      component: PrivacySettingsModal,
      shouldCloseOnOverlayClick: false,
      shouldCloseOnEsc: false,
    });

    context.actions.createModal(modalState);
  };

  const handleEditLanguageClick = (): void => {
    setOpen(false);
    setLanguageSettingsOpen(true);
  };

  const handleEditLanguageClose = (): void => {
    setLanguageSettingsOpen(false);
  };

  const handleEditProfileClick = (): void => {
    setOpen(false);

    const { state: appState } = context;
    const { users, roles, projects, selectedProject } = appState;

    const user = users.find((u: any) => userId === u.id);

    const project = projects.get(selectedProject);

    if (!project) {
      return;
    }

    const userProfileModalState = new ModalState({
      id: 'userProfileModal',
      portal: 'main',
      open: true,
      component: EditUserProfileInfoModal,
      shouldCloseOnOverlayClick: false,
      shouldCloseOnEsc: false,
      props: {
        user,
        roles,
        project,
      },
    });

    context.actions.createModal(userProfileModalState);
  };

  return (
    <Manager>
      <LanguageSettingsModal
        open={languageSettingsOpen}
        onDismiss={handleEditLanguageClose}
      />
      <Reference>
        {({ ref }): React.ReactNode => (
          <div
            ref={ref}
            id="user-circle"
            className="user-circle cursor-pointer"
            onClick={handleUserCircleClick}
          >
            <span className="wrem-2 hrem-2 flex-center">
              <Profile
                width="24px"
                height="24px"
                style={{ fill: '#4D555C', stroke: 'transparent' }}
              />
            </span>
          </div>
        )}
      </Reference>
      <Popper placement="bottom-end">
        {({ ref, style, placement }): React.ReactNode => {
          return open ? (
            <div
              id="user-circle-popover-overlay"
              className="bgd-white wrem-11 b-2 profile-menu"
              ref={ref}
              style={{
                ...style,
                zIndex: 11,
                width: 232,
              }}
              data-placement={placement}
            >
              <OutsideClick onOutsideClick={handleOutsideClick}>
                <div className="column items-center color-profile-dark user-menu">
                  {selectedProject && selectedProject !== '' ? (
                    <div
                      id="user-circle-popover-logout-btn"
                      className="item"
                      onClick={handleEditProfileClick}
                    >
                      {translate('userDropdown:editProfile')}
                    </div>
                  ) : null}
                  <div
                    id="user-circle-popover-edit-privacy-btn"
                    className="item"
                    onClick={handleEditPrivacySettingsClick}
                  >
                    {translate('userDropdown:editPrivacySettings')}
                  </div>

                  <a
                    href={accountLink}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: '#373d42', textDecoration: 'none' }}
                    id="user-circle-popover-edit-account-btn"
                    className="item"
                  >
                    {translate('userDropdown:editAccount')}{' '}
                    <NewWindow fill="#373d42" width="20" height="19" />
                  </a>

                  {isManageAccountIntegrationsEnabled && (
                    <div
                      className={buildClassName('item', {
                        'text-light-gray hover:cursor-default hover:!bg-white':
                          !canManageConnectedAccounts,
                      })}
                      onClick={
                        canManageConnectedAccounts
                          ? () => history.push('/connected-accounts')
                          : () => undefined
                      }
                      id="user-circle-popover-connected-accounts-btn"
                    >
                      {translate('userDropdown:connectedAccounts')}
                    </div>
                  )}

                  <div
                    className="item"
                    onClick={handleEditLanguageClick}
                    id="user-circle-popover-language-btn"
                  >
                    <div>
                      {translate('userDropdown:language')}
                      <span className="label">
                        {selectedLanguage?.label}{' '}
                        {selectedLanguage?.description}
                      </span>
                    </div>

                    <LanguageIcon width="20" height="19" />
                  </div>

                  <div
                    id="user-circle-popover-logout-btn"
                    className="item"
                    onClick={handleLogoutClick}
                    style={{ color: '#a71629' }}
                    data-testid="user-circle-popover-logout-menu"
                  >
                    {translate('userDropdown:logOut')}
                  </div>
                </div>
              </OutsideClick>
            </div>
          ) : null;
        }}
      </Popper>
    </Manager>
  );
};

export default withRouter(withProjectManager(UserCircle));
